(() => {
  const SUCCESS_MESSAGE_SELECTOR = [
    '.alert.alert-success',
    '.alert-success',
    '.form-success',
    '.form-message.success',
    '.form-message--success',
  ].join(', ');

  const ERROR_MESSAGE_SELECTOR = [
    '.alert.alert-error',
    '.alert-error',
    '.form-error',
    '.form-message.error',
    '.form-message--error',
    '.has-error',
  ].join(', ');

  const notifyPdfViewerFormSuccess = () => {
    document.dispatchEvent(new Event('pdf-viewer-form-success'));
  };

  if (!window.pdfViewerModalAfterSubmitWrapped) {
    const originalAfterSubmit = window.afterSubmit;

    window.afterSubmit = function (...args) {
      const result = typeof originalAfterSubmit === 'function' ? originalAfterSubmit.apply(this, args) : true;

      notifyPdfViewerFormSuccess();

      return result;
    };

    window.pdfViewerModalAfterSubmitWrapped = true;
  }

  const getPdfDownloadFileName = (link) => {
    if (link.dataset.pdfDownloadFilename) {
      return link.dataset.pdfDownloadFilename;
    }

    try {
      const url = new URL(link.href);
      return url.pathname.split('/').filter(Boolean).pop() || 'download.pdf';
    } catch (error) {
      return 'download.pdf';
    }
  };

  document.addEventListener('click', async (event) => {
    const link = event.target.closest('[data-pdf-download]');

    if (!link) {
      return;
    }

    event.preventDefault();

    try {
      const response = await fetch(link.href);

      if (!response.ok) {
        throw new Error(`PDF download failed with ${response.status}`);
      }

      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const downloadLink = document.createElement('a');
      downloadLink.href = objectUrl;
      downloadLink.download = getPdfDownloadFileName(link);
      document.body.append(downloadLink);
      downloadLink.click();
      downloadLink.remove();

      window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
    } catch (error) {
      console.error(error);
      window.location.href = link.href;
    }
  });

  if (!customElements.get('pdf-viewer-modal')) {
    class PdfViewerModal extends HTMLElement {
      constructor() {
        super();
        this.openedBy = null;
        this.pendingSubmit = false;

        this.handleClick = this.handleClick.bind(this);
        this.handleKeyup = this.handleKeyup.bind(this);
        this.handleSubmit = this.handleSubmit.bind(this);
        this.handleSubmitClick = this.handleSubmitClick.bind(this);
        this.handleMutation = this.handleMutation.bind(this);
        this.handleExternalSuccess = this.handleExternalSuccess.bind(this);
      }

      connectedCallback() {
        this.dialog = this.querySelector('[role="dialog"]');
        this.form = this.querySelector('form');
        this.submitButton = this.form ? this.form.querySelector('button[type="submit"], input[type="submit"]') : null;
        this.successObserver = new MutationObserver(this.handleMutation);

        if (!this.hasAttribute('open')) {
          this.hidden = true;
        }

        this.addEventListener('click', this.handleClick);
        document.addEventListener('pdf-viewer-form-success', this.handleExternalSuccess);

        if (this.form) {
          this.form.addEventListener('submit', this.handleSubmit);
          this.successObserver.observe(this.form, { childList: true, subtree: true });
        }

        if (this.submitButton) {
          this.submitButton.addEventListener('click', this.handleSubmitClick);
        }
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.handleClick);
        this.removeEventListener('keyup', this.handleKeyup);
        document.removeEventListener('pdf-viewer-form-success', this.handleExternalSuccess);

        if (this.form) {
          this.form.removeEventListener('submit', this.handleSubmit);
        }

        if (this.submitButton) {
          this.submitButton.removeEventListener('click', this.handleSubmitClick);
        }

        if (this.successObserver) {
          this.successObserver.disconnect();
        }
      }

      handleClick(event) {
        const closeButton = event.target.closest('[data-pdf-modal-close]');

        if (event.target !== this && !closeButton) {
          return;
        }

        this.close();
      }

      handleKeyup(event) {
        if (event.key === 'Escape') {
          this.close();
        }
      }

      handleSubmit(event) {
        if (!this.form || event.target !== this.form) {
          return;
        }

        this.pendingSubmit = this.form.checkValidity();
      }

      handleSubmitClick() {
        if (!this.form) {
          return;
        }

        this.pendingSubmit = this.form.checkValidity();
      }

      handleMutation() {
        if (!this.pendingSubmit) {
          return;
        }

        if (this.querySelector(SUCCESS_MESSAGE_SELECTOR)) {
          this.closeAfterSuccess();
          return;
        }

        if (this.querySelector(ERROR_MESSAGE_SELECTOR)) {
          this.pendingSubmit = false;
        }
      }

      handleExternalSuccess() {
        if (!this.pendingSubmit || !this.hasAttribute('open')) {
          return;
        }

        this.closeAfterSuccess();
      }

      clearSuccessMessages() {
        this.querySelectorAll(SUCCESS_MESSAGE_SELECTOR).forEach((message) => message.remove());
      }

      syncCustomDropdowns() {
        const selects = Array.from(this.querySelectorAll('select'));

        this.querySelectorAll('.custom-dropdown-hld[data-original-select]').forEach((dropdown) => {
          const select = selects.find((item) => item.id === dropdown.getAttribute('data-original-select'));
          const selectedOption = select ? select.options[select.selectedIndex] : null;
          const summary = dropdown.querySelector('summary');

          if (!select || !selectedOption || !summary) {
            return;
          }

          summary.innerHTML = selectedOption.innerHTML;
          dropdown.querySelectorAll('.custom-dropdown__list button').forEach((button) => {
            if (button.getAttribute('data-value') === select.value) {
              button.setAttribute('aria-selected', 'true');
            } else {
              button.removeAttribute('aria-selected');
            }
          });
        });
      }

      closeAfterSuccess() {
        this.pendingSubmit = false;

        if (this.form) {
          this.form.reset();
          this.syncCustomDropdowns();
        }

        window.requestAnimationFrame(() => this.close());
      }

      open(opener) {
        this.openedBy = opener;
        this.pendingSubmit = false;
        this.clearSuccessMessages();
        this.hidden = false;
        this.setAttribute('open', '');
        this.setAttribute('aria-hidden', 'false');
        document.body.classList.add('overflow-hidden');
        document.body.classList.add('pdf-viewer-modal-open');
        window.dispatchEvent(new Event('initCustomDropdowns'));

        if (this.openedBy) {
          this.openedBy.setAttribute('aria-expanded', 'true');
        }

        this.addEventListener('keyup', this.handleKeyup);

        window.requestAnimationFrame(() => {
          if (typeof trapFocus === 'function') {
            trapFocus(this, this.dialog || this);
          } else if (this.dialog) {
            this.dialog.focus();
          }
        });
      }

      close() {
        if (!this.hasAttribute('open')) {
          return;
        }

        this.removeAttribute('open');
        this.setAttribute('aria-hidden', 'true');
        this.hidden = true;
        document.body.classList.remove('overflow-hidden');
        document.body.classList.remove('pdf-viewer-modal-open');
        this.removeEventListener('keyup', this.handleKeyup);
        this.pendingSubmit = false;

        if (this.openedBy) {
          this.openedBy.setAttribute('aria-expanded', 'false');
        }

        if (typeof removeTrapFocus === 'function') {
          removeTrapFocus(this.openedBy);
        } else if (this.openedBy) {
          this.openedBy.focus();
        }
      }
    }

    customElements.define('pdf-viewer-modal', PdfViewerModal);
  }

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-pdf-modal-open]');

    if (!opener) {
      return;
    }

    const modalId = opener.getAttribute('aria-controls');
    const modal = modalId ? document.getElementById(modalId) : null;

    if (!modal || typeof modal.open !== 'function') {
      return;
    }

    event.preventDefault();
    modal.open(opener);
  });

  if (customElements.get('pdf-document-viewer')) {
    return;
  }

  class PdfDocumentViewer extends HTMLElement {
    static pdfjsPromise = null;

    constructor() {
      super();
      this.pdf = null;
      this.loadingTask = null;
      this.currentSpread = 0;
      this.renderToken = 0;
      this.resizeTimer = null;
      this.resizeObserver = null;
      this.lastReaderWidth = 0;
      this.lastReaderHeight = 0;

      this.handleResize = this.handleResize.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
      this.handlePointerDown = this.handlePointerDown.bind(this);
      this.handlePointerUp = this.handlePointerUp.bind(this);
      this.handlePreviousClick = this.handlePreviousClick.bind(this);
      this.handleNextClick = this.handleNextClick.bind(this);
    }

    connectedCallback() {
      this.reader = this.querySelector('[data-pdf-reader]');
      this.stage = this.querySelector('[data-pdf-stage]');
      this.status = this.querySelector('[data-pdf-status]');
      this.dots = this.querySelector('[data-pdf-dots]');
      this.loader = this.querySelector('[data-pdf-loading]');
      this.previousButtons = Array.from(this.querySelectorAll('[data-pdf-arrow-prev]'));
      this.nextButtons = Array.from(this.querySelectorAll('[data-pdf-arrow-next]'));
      this.pdfSrc = this.dataset.pdfSrc;
      this.pagesPerView = this.normalizePagesPerView(this.dataset.pagesPerView);
      this.pageLabelPrefix = this.dataset.pageLabelPrefix || 'SEITE';
      this.loadingLabel = this.dataset.loadingLabel || 'PDF wird geladen';
      this.errorLabel = this.dataset.errorLabel || 'PDF konnte nicht geladen werden.';
      this.pointerStartX = 0;

      window.addEventListener('resize', this.handleResize);

      if (this.reader) {
        this.reader.addEventListener('keydown', this.handleKeydown);
        this.reader.addEventListener('pointerdown', this.handlePointerDown);
        this.reader.addEventListener('pointerup', this.handlePointerUp);
        this.lastReaderWidth = this.reader.clientWidth;
        this.lastReaderHeight = this.reader.clientHeight;

        if ('ResizeObserver' in window) {
          this.resizeObserver = new ResizeObserver((entries) => {
            const entry = entries[0];
            const width = entry.contentRect.width;
            const height = entry.contentRect.height;

            if (Math.abs(width - this.lastReaderWidth) < 1 && Math.abs(height - this.lastReaderHeight) < 1) {
              return;
            }

            this.lastReaderWidth = width;
            this.lastReaderHeight = height;
            this.handleResize();
          });
          this.resizeObserver.observe(this.reader);
        }
      }

      this.previousButtons.forEach((button) => button.addEventListener('click', this.handlePreviousClick));
      this.nextButtons.forEach((button) => button.addEventListener('click', this.handleNextClick));

      if (this.pdfSrc) {
        this.loadPdf();
      }
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.handleResize);
      window.removeEventListener('load', this.handleResize);

      if (this.reader) {
        this.reader.removeEventListener('keydown', this.handleKeydown);
        this.reader.removeEventListener('pointerdown', this.handlePointerDown);
        this.reader.removeEventListener('pointerup', this.handlePointerUp);
      }

      this.previousButtons.forEach((button) => button.removeEventListener('click', this.handlePreviousClick));
      this.nextButtons.forEach((button) => button.removeEventListener('click', this.handleNextClick));

      window.clearTimeout(this.resizeTimer);
      this.renderToken += 1;

      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
      }

      if (this.loadingTask && typeof this.loadingTask.destroy === 'function') {
        this.loadingTask.destroy();
      }
    }

    normalizePagesPerView(value) {
      const parsedValue = Number.parseInt(value, 10);
      return parsedValue === 1 ? 1 : 2;
    }

    async loadPdfJs() {
      if (!PdfDocumentViewer.pdfjsPromise) {
        PdfDocumentViewer.pdfjsPromise = import(this.dataset.pdfjsSrc).then((pdfjsLib) => {
          pdfjsLib.GlobalWorkerOptions.workerSrc = this.dataset.pdfjsWorkerSrc;
          return pdfjsLib;
        });
      }

      return PdfDocumentViewer.pdfjsPromise;
    }

    async loadPdf() {
      try {
        this.setLoadingState(true);
        const pdfjsLib = await this.loadPdfJs();
        this.loadingTask = pdfjsLib.getDocument({ url: this.pdfSrc });
        this.pdf = await this.loadingTask.promise;
        this.totalPages = this.pdf.numPages;
        this.totalSpreads = Math.ceil(this.totalPages / this.pagesPerView);
        this.currentSpread = Math.min(this.currentSpread, this.totalSpreads - 1);
        this.renderDots();
        await this.waitForReaderLayout();
        await this.renderSpread();
        this.scheduleSettledRender();
      } catch (error) {
        this.showError(error);
      }
    }

    setLoadingState(isLoading) {
      this.classList.toggle('is-loading', isLoading);

      if (this.loader) {
        this.loader.textContent = isLoading ? this.loadingLabel : '';
      }
    }

    showError(error) {
      console.error(error);
      this.setLoadingState(false);
      this.classList.add('has-error');

      if (this.stage) {
        this.stage.innerHTML = '';
      }

      if (this.status) {
        this.status.textContent = this.errorLabel;
      }
    }

    handleResize() {
      window.clearTimeout(this.resizeTimer);
      this.resizeTimer = window.setTimeout(() => this.renderSpread(), 160);
    }

    waitForFrame() {
      return new Promise((resolve) => {
        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(resolve);
        });
      });
    }

    async waitForReaderLayout() {
      for (let attempt = 0; attempt < 10; attempt += 1) {
        await this.waitForFrame();

        if (!this.reader) {
          return;
        }

        const rect = this.reader.getBoundingClientRect();

        if (rect.width > 0 && rect.height > 0) {
          return;
        }
      }
    }

    scheduleSettledRender() {
      if (!this.pdf) {
        return;
      }

      this.handleResize();

      window.addEventListener('load', this.handleResize, { once: true });
      window.setTimeout(() => this.handleResize(), 400);
    }

    handleKeydown(event) {
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.goToSpread(this.currentSpread + 1);
      }

      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.goToSpread(this.currentSpread - 1);
      }
    }

    handlePointerDown(event) {
      this.pointerStartX = event.clientX;
    }

    handlePointerUp(event) {
      const swipeDistance = event.clientX - this.pointerStartX;

      if (Math.abs(swipeDistance) < 48) {
        return;
      }

      this.goToSpread(this.currentSpread + (swipeDistance < 0 ? 1 : -1));
    }

    handlePreviousClick() {
      this.goToSpread(this.currentSpread - 1);
    }

    handleNextClick() {
      this.goToSpread(this.currentSpread + 1);
    }

    goToSpread(spreadIndex) {
      if (!this.pdf || spreadIndex < 0 || spreadIndex >= this.totalSpreads || spreadIndex === this.currentSpread) {
        return;
      }

      this.currentSpread = spreadIndex;
      this.updateStatus();
      this.updateDots();
      this.renderSpread();
    }

    getVisiblePageNumbers() {
      const firstPage = this.currentSpread * this.pagesPerView + 1;
      const pages = [];

      for (let pageNumber = firstPage; pageNumber < firstPage + this.pagesPerView; pageNumber += 1) {
        if (pageNumber <= this.totalPages) {
          pages.push(pageNumber);
        }
      }

      return pages;
    }

    async renderSpread() {
      if (!this.pdf || !this.stage || !this.reader) {
        return;
      }

      const pageNumbers = this.getVisiblePageNumbers();
      const token = this.renderToken + 1;
      const readerRect = this.reader.getBoundingClientRect();
      const pageWidth = readerRect.width / pageNumbers.length;
      const pageHeight = readerRect.height;
      const hasRenderedPages = this.stage.children.length > 0;

      if (pageWidth <= 0 || pageHeight <= 0) {
        return;
      }

      this.renderToken = token;
      this.classList.add('is-rendering');

      if (!hasRenderedPages) {
        this.setLoadingState(true);
      }

      this.stage.style.setProperty('--pdf-viewer-page-count', pageNumbers.length);

      const nextPages = document.createDocumentFragment();
      const slots = pageNumbers.map((pageNumber) => {
        const slot = document.createElement('div');
        const canvas = document.createElement('canvas');
        slot.className = 'pdf-viewer__page';
        slot.dataset.pageNumber = pageNumber;
        slot.append(canvas);
        nextPages.append(slot);

        return { pageNumber, slot, canvas };
      });

      await Promise.all(
        slots.map(({ pageNumber, canvas }) =>
          this.renderPage(pageNumber, { width: pageWidth, height: pageHeight }, canvas, token)
        )
      );

      if (token !== this.renderToken) {
        return;
      }

      this.stage.replaceChildren(nextPages);
      this.setLoadingState(false);
      this.classList.remove('is-rendering');
      this.updateStatus();
      this.updateDots();
    }

    async renderPage(pageNumber, slotSize, canvas, token) {
      const page = await this.pdf.getPage(pageNumber);

      if (token !== this.renderToken) {
        return;
      }

      const viewport = page.getViewport({ scale: 1 });
      const fitScale = Math.min(slotSize.width / viewport.width, slotSize.height / viewport.height);
      const renderViewport = page.getViewport({ scale: fitScale });
      const outputScale = Math.min(window.devicePixelRatio || 1, 2);
      const context = canvas.getContext('2d', { alpha: false });

      canvas.width = Math.floor(renderViewport.width * outputScale);
      canvas.height = Math.floor(renderViewport.height * outputScale);
      canvas.style.width = `${renderViewport.width}px`;
      canvas.style.height = `${renderViewport.height}px`;

      context.setTransform(outputScale, 0, 0, outputScale, 0, 0);

      await page.render({
        canvasContext: context,
        viewport: renderViewport,
      }).promise;
    }

    updateStatus() {
      if (!this.status || !this.totalPages) {
        return;
      }

      const firstPage = this.currentSpread * this.pagesPerView + 1;
      const lastPage = Math.min(firstPage + this.pagesPerView - 1, this.totalPages);
      const pageRange = firstPage === lastPage ? `${firstPage}` : `${firstPage} - ${lastPage}`;
      this.status.textContent = `${this.pageLabelPrefix} ${pageRange} / ${this.totalPages}`;
    }

    renderDots() {
      if (!this.dots || !this.totalSpreads) {
        return;
      }

      this.dots.innerHTML = '';

      for (let spreadIndex = 0; spreadIndex < this.totalSpreads; spreadIndex += 1) {
        const button = document.createElement('button');
        const firstPage = spreadIndex * this.pagesPerView + 1;
        const lastPage = Math.min(firstPage + this.pagesPerView - 1, this.totalPages);
        const labelRange = firstPage === lastPage ? `${firstPage}` : `${firstPage} - ${lastPage}`;
        button.className = 'pdf-viewer__dot';
        button.type = 'button';
        button.setAttribute('aria-label', `${this.pageLabelPrefix} ${labelRange}`);
        button.addEventListener('click', () => this.goToSpread(spreadIndex));
        this.dots.append(button);
      }

      this.updateDots();
    }

    updateDots() {
      if (!this.dots) {
        return;
      }

      Array.from(this.dots.children).forEach((dot, index) => {
        const isActive = index === this.currentSpread;
        dot.classList.toggle('is-active', isActive);
        dot.toggleAttribute('aria-current', isActive);
      });

      this.updateNavigation();
    }

    updateNavigation() {
      const isAtStart = this.currentSpread <= 0;
      const isAtEnd = !this.totalSpreads || this.currentSpread >= this.totalSpreads - 1;

      this.previousButtons.forEach((button) => this.updateNavigationButton(button, isAtStart));
      this.nextButtons.forEach((button) => this.updateNavigationButton(button, isAtEnd));
    }

    updateNavigationButton(button, isDisabled) {
      if (!button) {
        return;
      }

      button.disabled = isDisabled;
      button.classList.toggle('swiper-button-disabled', isDisabled);
      button.setAttribute('aria-disabled', isDisabled ? 'true' : 'false');
    }
  }

  customElements.define('pdf-document-viewer', PdfDocumentViewer);
})();
